import React, { createContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

// Context to manage selected organization, facility, and unit data across components
export const FacilityUnitContext = createContext();

export const FacilityUnitProvider = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const isAuthInitializing = useSelector((state) => state.auth.isAuthInitializing);

  const storedOrganization = JSON.parse(sessionStorage.getItem('selectedOrganization')) || null;
  const storedFacility = JSON.parse(sessionStorage.getItem('selectedFacility')) || null;
  const storedUnit = JSON.parse(sessionStorage.getItem('selectedUnit')) || null;

  // State to track selected organization, facility, unit, and battery percentage
  const [selectedOrganization, setSelectedOrganization] = useState(storedOrganization);
  const [selectedFacility, setSelectedFacility] = useState(storedFacility);
  const [selectedUnit, setSelectedUnit] = useState(storedUnit);
  const [batteryPercentage, setBatteryPercentage] = useState(0);
  const [loadingBattery, setLoadingBattery] = useState(false);

  // Save the selected organization to sessionStorage whenever it changes
  useEffect(() => {
    if (selectedOrganization !== null) {
      sessionStorage.setItem('selectedOrganization', JSON.stringify(selectedOrganization));
    } else {
      sessionStorage.removeItem('selectedOrganization');
    }
  }, [selectedOrganization]);

  // Save the selected facility to sessionStorage whenever it changes
  useEffect(() => {
    if (selectedFacility !== null) {
      sessionStorage.setItem('selectedFacility', JSON.stringify(selectedFacility));
    } else {
      sessionStorage.removeItem('selectedFacility');
    }
  }, [selectedFacility]);

  // Save the selected unit to sessionStorage whenever it changes
  useEffect(() => {
    if (selectedUnit !== null) {
      sessionStorage.setItem('selectedUnit', JSON.stringify(selectedUnit));
    } else {
      sessionStorage.removeItem('selectedUnit');
    }
  }, [selectedUnit]);

  // Clear state and sessionStorage when the user is not authenticated
  useEffect(() => {
    if (!isAuthInitializing && !isAuthenticated) {
      setSelectedOrganization(null);
      setSelectedFacility(null);
      setSelectedUnit(null);
      setBatteryPercentage(0);

      // Remove all stored selections from sessionStorage
      sessionStorage.removeItem('selectedOrganization');
      sessionStorage.removeItem('selectedFacility');
      sessionStorage.removeItem('selectedUnit');
    }
  }, [isAuthInitializing, isAuthenticated]);

  // Updates the selected organization and facility in the state and sessionStorage.
  const updateOrganizationAndFacility = (organizationId, facilityId) => {
    setSelectedOrganization(organizationId);
    setSelectedFacility(facilityId);

    // Persist updated selections to sessionStorage
    sessionStorage.setItem('selectedOrganization', JSON.stringify(organizationId));
    sessionStorage.setItem('selectedFacility', JSON.stringify(facilityId));
  };

  // Provide context values to children components
  return (
    <FacilityUnitContext.Provider
      value={{
        selectedOrganization,
        setSelectedOrganization,
        selectedFacility,
        setSelectedFacility,
        selectedUnit,
        setSelectedUnit,
        batteryPercentage,
        setBatteryPercentage,
        loadingBattery,
        setLoadingBattery,
        updateOrganizationAndFacility,
      }}
    >
      {children}
    </FacilityUnitContext.Provider>
  );
};
